import React, { useState, useMemo, useRef } from "react";
import { useTable, usePagination, useSortBy, useExpanded, useResizeColumns, useFlexLayout } from "react-table";
import { QueryClient, QueryClientProvider, useQuery } from 'react-query'
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination';
import { reducer, useEffectToDispatch, fetchApiData, initialState, Sorting } from "@/shared/apiTableHelper"
import { FUNDING_APPROVALS_URL, QUIET_REQUEST } from '@/service/fundingApprovals';
import { COLUMNS } from "./columns";
import FundingApprovalsFilter from "./FundingApprovalsFilter";

export const FORBIDDEN_MESSAGE = 'Super Admin access required.';

const DEFAULT_FILTER = { state: 'pending', keyword: '', program_id: '', held_from: '', held_to: '' };
const DEFAULT_SORT = [{ id: 'release_at', desc: false }];

// The app's axios interceptor resolves 4xx responses, so fetchApiData hands a 403 back as a
// payload carrying `status` (no rows). A rejection with a response status is handled too.
const isForbidden = (data, error) => data?.status === 403 || error?.response?.status === 403;

// The global flash is suppressed for this grid, so the server's reason (e.g. an expired login) is shown here.
const serverLoadError = (data) => {
    if (data?.status === 401) return 'Your session has expired. Please log in again.';
    const message = data?.full?.message ?? data?.full?.errors;
    return (typeof message === 'string' && message) || 'Unable to load funding approvals.';
};

const DataTable = ({ onReview }) => {
    const [filter, setFilter] = useState(DEFAULT_FILTER);
    const [useFilter, setUseFilter] = useState(false);
    const [trigger, setTrigger] = useState(0);
    const refresh = () => setTrigger(Date.now() / 1000);

    // Read through a ref so the columns are built once and the Review cell still calls the latest handlers.
    const actions = useRef();
    actions.current = { onReview, refresh };

    const columns = useMemo(() => [
        ...COLUMNS,
        {
            Header: "Actions",
            id: "action",
            accessor: "action",
            disableSortBy: true,
            width: 90,
            minWidth: 80,
            Cell: ({ row }) => (
                <button type="button" className='btn btn-primary btn-xs mb-0' onClick={() => actions.current.onReview && actions.current.onReview(row.original, actions.current.refresh)}>
                    Review
                </button>
            ),
        }
    ], [])

    const [{ queryPageIndex, queryPageSize, totalCount, queryPageFilter, queryPageSortBy, queryTrigger }, dispatch] =
        React.useReducer(reducer, { ...initialState, queryPageFilter: DEFAULT_FILTER, queryPageSortBy: DEFAULT_SORT });

    const apiUrl = FUNDING_APPROVALS_URL

    const { isLoading, error, data, isSuccess, isPreviousData } = useQuery(
        ['funding-approvals', apiUrl, queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy, queryTrigger],
        () => fetchApiData(
            {
                url: apiUrl,
                page: queryPageIndex,
                size: queryPageSize,
                filter: queryPageFilter,
                sortby: queryPageSortBy,
                trigger: queryTrigger,
                // The grid shows its own 403 / load error inline; skip the global flash.
                requestConfig: QUIET_REQUEST,
            }),
        {
            keepPreviousData: true,
            staleTime: Infinity,
            retry: false,
        }
    );

    const totalPageCount = Math.ceil(totalCount / queryPageSize)

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        pageCount,
        pageOptions,
        gotoPage,
        previousPage,
        canPreviousPage,
        nextPage,
        canNextPage,
        setPageSize,
        state: { pageIndex, pageSize, sortBy }
    } = useTable({
        columns,
        data: Array.isArray(data?.results) ? data.results : [],
        initialState: {
            pageIndex: queryPageIndex,
            pageSize: queryPageSize,
            sortBy: queryPageSortBy
        },
        manualPagination: true, // Tell the usePagination
        manualSortBy: true,
        disableSortRemove: true,
        pageCount: data ? totalPageCount : null,
        autoResetSortBy: false,
        autoResetExpanded: false,
        autoResetPage: false,
        onInvoiceClosed: refresh,
    },
        useSortBy,
        useExpanded,
        usePagination,
        useResizeColumns,
        useFlexLayout
    );

    const manualPageSize = []

    useEffectToDispatch(dispatch, { pageIndex, pageSize, gotoPage, sortBy, filter, data, useFilter, trigger });

    // Approving the only row on a later page removes it from Pending; step back instead of leaving an empty page with no pager.
    React.useEffect(() => {
        // pageIndex === queryPageIndex: only act once the empty result belongs to the page on screen,
        // otherwise the render right after gotoPage() steps back a second time.
        if (isSuccess && !isPreviousData && pageIndex === queryPageIndex && Array.isArray(data?.results) && data.results.length === 0 && pageIndex > 0) {
            gotoPage(pageIndex - 1);
        }
    }, [isSuccess, isPreviousData, data, pageIndex, queryPageIndex, gotoPage]);

    if (isForbidden(data, error)) {
        return <p>{FORBIDDEN_MESSAGE}</p>;
    }

    const loadError = error
        ? (error?.message || 'Unable to load funding approvals.')
        : (isSuccess && !Array.isArray(data?.results) ? serverLoadError(data) : null);

    return (
        <>
            <FundingApprovalsFilter
                onClickFilterCallback={(values) => setFilter(values)}
                setUseFilter={setUseFilter}
            />
            {loadError && <p role="alert" className="text-danger">Error: {loadError}</p>}
            {isLoading && !loadError && <p>Loading...</p>}
            <div className='table react-table funding-approvals-table'>
                {isSuccess && !loadError && (
                    <table {...getTableProps()} className="table">
                        <thead>
                            {headerGroups.map((headerGroup) => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column, i) => (
                                        <th className={`cell-column-${i}`} {...column.getHeaderProps(column.getSortByToggleProps())}>
                                            {column.render('Header')}
                                            {column.isSorted ? <Sorting column={column} /> : ''}
                                            <div
                                                {...column.getResizerProps()}
                                                className={`resizer ${column.isResizing ? 'isResizing' : ''}`}
                                            />
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody className="table table--bordered" {...getTableBodyProps()}>
                            {page.map(row => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map((cell, i) => (
                                            <td className={`cell-column-${i}`} {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                        ))}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>
            {isSuccess && !loadError && (rows.length > 0) && (
                    <>
                        <ReactTablePagination
                            page={page}
                            gotoPage={gotoPage}
                            previousPage={previousPage}
                            nextPage={nextPage}
                            canPreviousPage={canPreviousPage}
                            canNextPage={canNextPage}
                            pageOptions={pageOptions}
                            pageSize={pageSize}
                            pageIndex={pageIndex}
                            pageCount={pageCount}
                            setPageSize={setPageSize}
                            manualPageSize={manualPageSize}
                            dataLength={totalCount}
                        />
                        <div className="pagination justify-content-end mt-2">
                            <span>
                                Go to page:{' '}
                                <input
                                    type="number"
                                    value={pageIndex + 1}
                                    onChange={(e) => {
                                        const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                        gotoPage(page);
                                    }}
                                    style={{ width: '100px' }}
                                />
                            </span>{' '}
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                }}
                            >
                                {[10, 20, 30, 40, 50].map((size) => (
                                    <option key={size} value={size}>
                                        Show {size}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </>
                )}
        </>
    );
}

const TableWrapper = ({ onReview }) => {
    // One client per mounted grid so cached pages never leak between mounts.
    const [queryClient] = useState(() => new QueryClient());
    return (
        <QueryClientProvider client={queryClient}>
            <DataTable onReview={onReview} />
        </QueryClientProvider>
    )
}

export default TableWrapper;
